import { useRef, useState } from "react";
import { apiClient, newIdempotencyKey } from "../lib/api-client";
import { uploadToPresignedTarget } from "../lib/upload";
import { truncateFilename } from "../lib/format";
import { describeError } from "./States";

type Phase = "idle" | "uploading" | "finalizing" | "done" | "error";

export function UploadPanel({
  listingId,
  disabled,
  onUploaded,
}: {
  listingId: string;
  disabled?: boolean;
  onUploaded: () => void;
}) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [progress, setProgress] = useState(0);
  const [filename, setFilename] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleFile(file: File) {
    setFilename(file.name);
    setError(null);
    setProgress(0);
    setPhase("uploading");
    const idempotencyKey = newIdempotencyKey();
    try {
      const presigned = await apiClient.presignUpload(
        listingId,
        {
          filename: file.name,
          declaredMimeType: file.type || "application/octet-stream",
          sizeBytes: file.size,
        },
        idempotencyKey,
      );

      const result = await uploadToPresignedTarget(presigned, file, setProgress);

      setPhase("finalizing");
      await apiClient.completeUpload(listingId, presigned.uploadId, result.etag, idempotencyKey);

      setPhase("done");
      onUploaded();
    } catch (err) {
      setPhase("error");
      setError(describeError(err));
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div className="stack">
      <div className="field">
        <label htmlFor="listing-file">Product file</label>
        <input
          ref={inputRef}
          id="listing-file"
          type="file"
          disabled={disabled || phase === "uploading" || phase === "finalizing"}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
          }}
        />
        <span className="hint">
          The file is scanned for malware before it can be published. Replacing it starts a new
          scan.
        </span>
      </div>

      {filename && phase !== "idle" && (
        <div className="stack" aria-live="polite">
          <p className="text-sm">
            {phase === "uploading" && `Uploading ${truncateFilename(filename)}…`}
            {phase === "finalizing" && `Finalizing ${truncateFilename(filename)}…`}
            {phase === "done" && `Uploaded ${truncateFilename(filename)}. Waiting for scan…`}
            {phase === "error" && `Could not upload ${truncateFilename(filename)}.`}
          </p>
          {(phase === "uploading" || phase === "finalizing") && (
            <div
              className="progress"
              role="progressbar"
              aria-valuenow={Math.round(progress * 100)}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <span style={{ width: `${Math.round(progress * 100)}%` }} />
            </div>
          )}
          {phase === "error" && error && (
            <p className="field-error-text" role="alert">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
